import {
  BucketSize,
  TimeSeries as TimeSeriesData,
  TimeSeriesValue,
  bucketSizeToInterval
} from '@/api-client/models/indoorclimate/Contracts'
import { ChartComponent } from '..'
import { Axis, createAxis } from './axes'
import { createBackgroundGradient } from './backgroundGradient'
import { Chart } from './chart'
import { CircleHighlight, createCircleHightlights } from './circle-hightlight'
import { createHighlightLine, createLineBottomGradients, createLines } from './lines'
import { Ticks, createTicks, responsiveLabels } from './ticks'
import { createTooltip } from './tooltip'
import { calculateLimitsFromValues, combineLimits } from './limits'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import {
  ColorScaleConfig,
  ColorScaleInterpolate,
  ColorScaleStep,
  getColorForValue
} from '@/utils/colorScale'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { LegendComponent } from '../../LegendBase'
import { createRect } from './rect'
import { TimeSeries } from '@/components/AnalyticsDashboard/timeSeries'
import { LabelConfig } from '../../LegendBase/components/CheckboxComponent/CheckboxComponent.vue'
import { bucketSizeToFormat, bucketSizeToTooltipFormat } from '../utils'
import { calculateBucketSizes } from '@/utils/calculateBucketSizes'
import { createErrorText } from './utils'
import { MeasurementPointTS } from '@/components/AnalyticsDashboard/measurements/contracts'
import { DataSource } from '@/composables/useDataSources'
import { formatTimestamps } from './timeFormatter'
import { Ref, computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ChartBase from '../ChartBase.vue'
import { DashboardComponentType } from '@/features/dashboard'
import { useComponentData } from '@/components/AnalyticsDashboard/measurements/useComponentData'
import { useTimeSeriesResolution } from '@/components/AnalyticsDashboard/composables/useTimeSeriesResolution'
import { useTimeSelection } from '@/composables/useTimeRange'
import { useComponentFilter } from '@/components/AnalyticsDashboard/measurements/useMeasurements'
import md5 from 'md5'
import { useEntityLookup } from '@/composables/useEntityLookup'

const DEFAULT_DATA_FORMAT: FormatOptions = {
  format: {
    type: 'intl-number',
    style: 'decimal',
    maximumFractionDigits: 2
  }
}

const DEFAULT_HORIZONTAL_AXIS: Axis<'top' | 'bottom'> = {
  position: 'bottom',
  showTicks: false,
  showLine: false
}

const DEFAULT_VERTICAL_AXIS: Axis<'left' | 'right'> = {
  position: 'left',
  showTicks: false,
  showLine: false,
  lines: true
}

export type LineFormat = {
  color?: string
  width?: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export type TimeSeriesChart = {
  verticalAxis?: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  dataFormat?: FormatOptions
  dataTicks?: Ticks
  timeFormat?: FormatOptions
  colorScale?: string
  gradientBelow?: boolean
  backgroundGradient?: boolean
  dataSources: string[]
  labels: LocaleString[]
  lineFormats: LineFormat[]
  missingDataMessage: LocaleString
}

const getTimeSeriesFromData = (timeSerieses: TimeSeriesValue[][]): TimeSeries[] =>
  timeSerieses
    .map((values) =>
      values.map((value) => ({ timestamp: new Date(value.timestamp), value: value.value }))
    )
    .map((values) => new TimeSeries(values))

const getTimeSeries = (
  chart: Chart<'timeseries'>,
  dataSource: Record<string, DataSource<TimeSeriesData | TimeSeriesValue[]> | undefined>
): TimeSeries[] =>
  getTimeSeriesFromData(
    chart.dataSources.map((source) => {
      const data = dataSource[source]?.data
      return data instanceof Array ? data : (data?.splits && data.splits[0]?.values) ?? []
    })
  )

const calculateBucketSizeFromTimeseries = (values: TimeSeriesValue[]): BucketSize | undefined => {
  if (values.length < 2) return undefined
  const first = new Date(values[0].timestamp)
  const second = new Date(values[1].timestamp)
  return calculateBucketSizes(first.toISOString(), second.toISOString())[0]
}

export function createTimeSeries(
  chart: Chart<'timeseries'>,
  dataSources: Record<string, DataSource<TimeSeriesData | TimeSeriesValue[]> | undefined>,
  viewport: { width: number; height: number },
  locale: string
): ChartComponent[] | undefined {
  if (chart.dataSources.length === 0) return undefined
  if (!chart.dataSources.every((source) => dataSources[source]?.data)) return undefined
  const timeSerieses = chart.dataSources.map((ds) => dataSources[ds]!.data)
  const firstTimeSerie = timeSerieses[0]!
  const bucketSize =
    firstTimeSerie instanceof Array
      ? calculateBucketSizeFromTimeseries(firstTimeSerie)
      : firstTimeSerie.aggregation.bucketSize
  if (!bucketSize) return []

  const labels = chart.labels.map((l) => l[locale])

  const timeSeries = getTimeSeries(chart, dataSources)

  const timeFormat: FormatOptions = bucketSizeToFormat[bucketSize]

  const tooltipFormat = bucketSizeToTooltipFormat[bucketSize]

  const dataFormat = chart.dataFormat ?? DEFAULT_DATA_FORMAT

  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }
  const horizontalTicks = timeSeries[0].getHorizontalTicks(viewport.width, timeFormat, locale)
  const verticalTicks = timeSeries[0].getVerticalTicks(dataFormat, locale, chart.dataTicks)
  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([...timeSeries.map((ts) => ts.indexLimits), tickLimits])

  const colorScale = (chart.colorScale ? dataSources[chart.colorScale]?.data : undefined) as
    | ColorScaleInterpolate
    | ColorScaleStep
    | undefined

  const missingDataRanges = timeSeries[0].getMissingDataRanges()
  const missingDataRects = missingDataRanges.map(([start, end]) =>
    createRect(
      {
        x: start,
        y: limits.y[0],
        width: end - start,
        height: limits.y[1] - limits.y[0],
        fill: '#0004'
      },
      limits
    )
  )

  const points = timeSeries.map((serie) => serie.indexedPoints)
  const colors = points.map((p, i) => p.map(() => chart.lineFormats[i].color ?? 'red'))
  const filteredPoints = points.map((p) => p.filter((p) => p[1] !== null) as [number, number][])

  if (filteredPoints.some((p) => p.length === 0))
    return createErrorText(chart.missingDataMessage, locale)

  const values = timeSeries
    .map((serie) => serie.points)
    .map((p) => p.filter((p) => p[1] !== null) as [number, number][])
  const allPoints = points.flatMap((p) => p).filter((p) => p[1] !== null) as [number, number][]
  const allColors = colors.flatMap((c) => c)
  const patterns = chart.lineFormats
    .map((f) =>
      f.style !== 'solid'
        ? {
            type: 'horizontal-stripes',
            color: colors[0][0],
            strokeWidth: 5
          }
        : undefined
    )
    .filter((p) => p !== undefined) as {
    type: 'horizontal-stripes'
    color: string
    strokeWidth: number
  }[]
  const allValues = values.flatMap((v) => v)
  const allLabels = labels.flatMap((l) => Array(filteredPoints.length).fill(l))
  const highlights: CircleHighlight[] = [
    {
      labels: allLabels,
      colors: allColors ?? allPoints.map(([, y]) => getColorForValue(colorScale!, y).toHex()),
      positions: allPoints,
      values: allValues,
      patterns,
      colorScale,
      visible: true
    }
  ]

  return [
    ...createHighlightLine(limits.x, 'vertical'),
    ...createTooltip(limits, dataFormat, tooltipFormat),
    ...(chart.backgroundGradient && colorScale
      ? createBackgroundGradient(colorScale, limits, 'vertical', 0.1)
      : []),
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...createLines(labels, chart.lineFormats, points, limits, colorScale),
    ...(chart.gradientBelow
      ? createLineBottomGradients(
          labels,
          chart.lineFormats.map((f) => f.color ?? 'red'),
          points,
          limits
        )
      : []),
    ...missingDataRects,
    ...createCircleHightlights(highlights, limits)
  ]
}

export type TimeSeriesConfig = {
  verticalAxis?: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  dataFormat?: FormatOptions
  dataTicks?: Ticks
  timeFormat?: FormatOptions
  colorScale?: ColorScaleConfig
  gradientBelow?: boolean
  backgroundGradient?: boolean
  dataSources: string[]
  missingDataMessage: LocaleString
}

type SeriesIdentifier = { path: string | null; field: string }

const defaultColors = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf'
]
function getDefaultColor(index: number): string {
  return defaultColors[index % defaultColors.length]
}

const excludedLabelGroupKeys = ['timestamp', 'realm_id', 'path']

function generateSeriesLabel(baseLabel: string, groupObj: Record<string, any>): string {
  const groupParts = Object.entries(groupObj)
    .filter(
      ([key, value]) =>
        value !== null && value !== undefined && !excludedLabelGroupKeys.includes(key)
    )
    .map(([key, value]) => `${key}: ${value}`)
    .sort()
    .join(', ')
  return groupParts ? `${baseLabel} (${groupParts})` : baseLabel
}

const normalizePath = (p: string | null | undefined): string | undefined => {
  if (p === null || p === undefined || p === '') {
    return undefined
  }
  return p
}

export function useTimeSeries(
  chartBase: Ref<InstanceType<typeof ChartBase> | undefined>,
  chartConfig: Ref<TimeSeriesConfig>,
  activeSeries: Ref<SeriesIdentifier[]>,
  featureName: string
) {
  const timeSelection = useTimeSelection()
  const componentFilters = useComponentFilter(featureName, {
    query: {
      groupBy: [{ type: 'timestamp_bucket', interval: bucketSizeToInterval['DAY'] }]
    }
  })

  const {
    bucketSize,
    buttons: resolutionButtons,
    selectedName: selectedResolution
  } = useTimeSeriesResolution(timeSelection, componentFilters, () => {})
  const { getEntityDisplayStringFromPath } = useEntityLookup()

  const { data, isLoading, measurements } = useComponentData<MeasurementPointTS[]>(
    featureName,
    DashboardComponentType.TimeSeries,
    'values'
  )
  const { locale } = useI18n()
  const viewport = ref<DOMRect | undefined>(undefined)

  const updateViewport = () => {
    viewport.value = chartBase.value?.$el.getBoundingClientRect()
  }

  const viewportObserver = new ResizeObserver(updateViewport)

  onUnmounted(() => {
    viewportObserver.disconnect()
  })

  watch(
    chartBase,
    (newChartBase) => {
      viewportObserver.disconnect()
      if (newChartBase?.$el) {
        updateViewport()
        viewportObserver.observe(newChartBase.$el)
      }
    },
    { immediate: true }
  )

  const isSingleFieldType = computed(() => measurements.value.length === 1)

  const seriesLineFormats = computed<LineFormat[]>(() =>
    activeSeries.value.map((series, index) => {
      const measurement = measurements.value.find((m) => m.field.name === series.field)
      return {
        color: measurement?.options?.color ?? getDefaultColor(index),
        style: 'solid',
        width: 1
      }
    })
  )

  const mergedData = computed<MeasurementPointTS[]>((): MeasurementPointTS[] => {
    if (!data.value || data.value.length === 0) return []

    const flattenedData = data.value
      .flat()
      .filter((x): x is MeasurementPointTS => x && x.timestamp !== undefined)

    const grouped = flattenedData.reduce((acc, point) => {
      const groupEntries = Object.entries(point.groups || {}).sort(([k1], [k2]) =>
        k1.localeCompare(k2)
      )
      const groupKeySource = JSON.stringify(groupEntries)
      const groupHash = md5(groupKeySource) || 'no_groups'

      const pointTimestamp =
        typeof point.timestamp === 'string' ? new Date(point.timestamp).getTime() : point.timestamp
      const key = `${pointTimestamp}-${groupHash}`

      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(point)
      return acc
    }, {} as Record<string, MeasurementPointTS[]>)

    return Object.values(grouped)
      .map((pointsInGroup) => {
        if (pointsInGroup.length === 0) return null

        return pointsInGroup.reduce((mergedPoint, currentPoint) => {
          if (Object.keys(mergedPoint).length === 0) {
            return { ...currentPoint }
          }
          return {
            ...mergedPoint,
            path: mergedPoint.path ?? currentPoint.path,
            tags: { ...(mergedPoint.tags || {}), ...(currentPoint.tags || {}) },
            fields: { ...(mergedPoint.fields || {}), ...(currentPoint.fields || {}) }
          }
        }, {} as Partial<MeasurementPointTS>) as MeasurementPointTS
      })
      .filter((p) => p?.timestamp !== undefined) as MeasurementPointTS[]
  })

  const seriesBaseLabels = computed<LocaleString[]>(() =>
    activeSeries.value.map((series) => {
      const measurement = measurements.value.find((m) => m.field.name === series.field)
      const measurementTitle = measurement?.title?.[locale.value] ?? series.field
      let labelText: string
      const seriesPath = normalizePath(series.path)

      if (isSingleFieldType.value) {
        labelText = seriesPath
          ? getEntityDisplayStringFromPath(seriesPath) ?? seriesPath
          : measurementTitle
      } else {
        if (seriesPath) {
          const entityName = getEntityDisplayStringFromPath(seriesPath) ?? seriesPath
          labelText = `${entityName} - ${measurementTitle}`
        } else {
          labelText = measurementTitle
        }
      }
      const labelObj: Partial<Record<string, string>> = {}
      labelObj[locale.value] = labelText
      return labelObj as LocaleString
    })
  )

  const components = computed(() => {
    if (!viewport.value) {
      return createErrorText(
        chartConfig.value.missingDataMessage || 'Chart viewport not available',
        locale.value
      )
    }
    if (activeSeries.value.length === 0) {
      return createErrorText(
        chartConfig.value.missingDataMessage || 'No active series selected',
        locale.value
      )
    }
    if (mergedData.value.length === 0 && !isLoading.value) {
      // Only show "no data" if not loading
      return createErrorText(
        chartConfig.value.missingDataMessage || 'No data available',
        locale.value
      )
    }
    if (isLoading.value && mergedData.value.length === 0) {
      return createErrorText({ en: 'Loading data...', sv: 'Laddar data...' }, locale.value)
    }

    const dataSorted = mergedData.value.slice().sort((a, b) => {
      const timeA = new Date(a.groups?.timestamp as string).getTime()
      const timeB = new Date(b.groups?.timestamp as string).getTime()
      return timeA - timeB
    })

    if (dataSorted.length === 0 && !isLoading.value) {
      return createErrorText(
        chartConfig.value.missingDataMessage || 'No data available after processing',
        locale.value
      )
    }

    const tooltipFormat: FormatOptions = {
      format: {
        type: 'intl-datetime',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour:
          bucketSize.value === 'HOUR' || bucketSize.value === 'MINUTE_10' ? '2-digit' : undefined,
        minute: bucketSize.value === 'MINUTE_10' ? '2-digit' : undefined
      }
    }
    const dataFormat = chartConfig.value.dataFormat ?? DEFAULT_DATA_FORMAT
    const horizontalAxisConfig: Axis<'top' | 'bottom'> = {
      ...DEFAULT_HORIZONTAL_AXIS,
      ...chartConfig.value.horizontalAxis
    }
    const verticalAxisConfig: Axis<'left' | 'right'> = {
      ...DEFAULT_VERTICAL_AXIS,
      ...chartConfig.value.verticalAxis
    }

    const uniqueTimestamps = Array.from(
      new Set(
        dataSorted
          .map((point) =>
            point.groups?.timestamp ? new Date(point.groups.timestamp as string).getTime() : NaN
          )
          .filter((ts) => !isNaN(ts))
      )
    ).sort((a, b) => a - b)

    if (uniqueTimestamps.length === 0 && !isLoading.value) {
      return createErrorText(
        chartConfig.value.missingDataMessage || 'No valid timestamps in data',
        locale.value
      )
    }

    const finalChartSeries: {
      points: [number, number | null][]
      label: string
      color: string
      lineFormat: LineFormat
    }[] = []
    const allYValues: number[] = []

    activeSeries.value.forEach((seriesConfig, seriesIndex) => {
      const fieldToPlot = seriesConfig.field
      const baseLabelText =
        seriesBaseLabels.value[seriesIndex]?.[locale.value] ?? `Field ${fieldToPlot}`
      const lineFormat = seriesLineFormats.value[seriesIndex]
      const normalizedSeriesConfigPath = normalizePath(seriesConfig.path)

      const relevantDataPoints = dataSorted.filter((point) => {
        const normalizedPointPath = normalizePath(point.path)

        const fieldExists = point.fields && typeof point.fields[fieldToPlot] !== 'undefined'
        const pathMatch = normalizedPointPath === normalizedSeriesConfigPath

        return fieldExists && pathMatch
      })

      const groupedBySubKeys = new Map<
        string,
        {
          groupObj: Record<string, any>
          points: { timestamp: number; value: number | null }[]
        }
      >()

      relevantDataPoints.forEach((point) => {
        const groupDefiningKeys: Record<string, any> = {}
        if (point.groups) {
          for (const key in point.groups) {
            if (!excludedLabelGroupKeys.includes(key)) {
              groupDefiningKeys[key] = point.groups[key]
            }
          }
        }
        const subGroupKey = JSON.stringify(
          Object.entries(groupDefiningKeys).sort(([k1], [k2]) => k1.localeCompare(k2))
        )

        const value = point.fields[fieldToPlot]
        const numericValue = typeof value === 'number' ? value : null
        const timestamp = point.groups?.timestamp
          ? new Date(point.groups.timestamp as string).getTime()
          : NaN

        if (!isNaN(timestamp)) {
          if (!groupedBySubKeys.has(subGroupKey)) {
            groupedBySubKeys.set(subGroupKey, { groupObj: groupDefiningKeys, points: [] })
          }
          groupedBySubKeys.get(subGroupKey)!.points.push({ timestamp, value: numericValue })
        }
      })

      groupedBySubKeys.forEach(({ groupObj, points: groupSpecificPoints }) => {
        const seriesDataMap = new Map<number, number | null>()
        groupSpecificPoints.forEach((p) => {
          seriesDataMap.set(p.timestamp, p.value)
          if (p.value !== null) {
            allYValues.push(p.value)
          }
        })

        const currentSeriesTrace: [number, number | null][] = uniqueTimestamps.map((ts) => [
          ts,
          seriesDataMap.get(ts) ?? null
        ])

        if (currentSeriesTrace.some((p) => p[1] !== null)) {
          finalChartSeries.push({
            points: currentSeriesTrace,
            label: generateSeriesLabel(baseLabelText, groupObj),
            color: lineFormat.color ?? getDefaultColor(finalChartSeries.length),
            lineFormat: lineFormat
          })
        }
      })
    })

    if (finalChartSeries.length === 0 && !isLoading.value) {
      return createErrorText(
        chartConfig.value.missingDataMessage || 'No series to display after filtering',
        locale.value
      )
    }
    if (finalChartSeries.length === 0 && isLoading.value) {
      return createErrorText({ en: 'Loading data...', sv: 'Loading data...' }, locale.value)
    }

    const finalSeriesPoints = finalChartSeries.map((s) => s.points)
    const finalSeriesLabels = finalChartSeries.map((s) => s.label)
    const finalSeriesLineFormats = finalChartSeries.map((s) => s.lineFormat)

    const horizontalTicks = responsiveLabels(
      { values: uniqueTimestamps, labels: formatTimestamps(uniqueTimestamps, locale.value) },
      viewport.value.width
    )

    const yMin = allYValues.length > 0 ? Math.min(...allYValues) : 0
    const yMax = allYValues.length > 0 ? Math.max(...allYValues) : 1
    const yLimits: [number, number] = [yMin, yMax]

    const verticalTicks = createTicks(
      chartConfig.value.dataTicks ?? 'auto',
      yLimits,
      allYValues,
      dataFormat,
      locale.value
    )

    const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
    const dataLimits = calculateLimitsFromValues(uniqueTimestamps, allYValues)
    const limits = combineLimits(
      allYValues.length > 0 || uniqueTimestamps.length > 0 ? [dataLimits, tickLimits] : [tickLimits]
    )

    const colorScale = chartConfig.value.colorScale as
      | ColorScaleInterpolate
      | ColorScaleStep
      | undefined

    const highlightPositions: [number, number][] = []
    const highlightValues: [number, number][] = []
    const highlightColors: string[] = []
    const highlightLabels: string[] = []

    finalChartSeries.forEach((series) => {
      series.points.forEach((point) => {
        if (point[1] !== null) {
          const position: [number, number] = [point[0], point[1]]
          highlightPositions.push(position)
          highlightValues.push(position)
          highlightColors.push(series.color)
          highlightLabels.push(series.label)
        }
      })
    })

    const patterns = finalChartSeries
      .map((s, i) =>
        s.lineFormat.style !== 'solid'
          ? {
              type: 'horizontal-stripes',
              color: s.color ?? getDefaultColor(i),
              strokeWidth: s.lineFormat.width ?? 1
            }
          : undefined
      )
      .filter((p): p is { type: 'horizontal-stripes'; color: string; strokeWidth: number } => !!p)

    const highlights: CircleHighlight[] =
      highlightPositions.length > 0
        ? [
            {
              labels: highlightLabels,
              colors: highlightColors,
              positions: highlightPositions,
              values: highlightValues,
              patterns: patterns.length > 0 ? patterns : undefined,
              colorScale,
              visible: true
            }
          ]
        : []

    const gradientColors = finalChartSeries.map((s, idx) => s.color ?? getDefaultColor(idx))

    return [
      ...createHighlightLine(limits.x, 'vertical'),
      ...createTooltip(limits, dataFormat, tooltipFormat),
      ...(chartConfig.value.backgroundGradient && colorScale
        ? createBackgroundGradient(colorScale, limits, 'vertical', 0.1)
        : []),
      ...createAxis(horizontalAxisConfig, horizontalTicks, limits.x),
      ...createAxis(verticalAxisConfig, verticalTicks, limits.y),
      ...createLines(
        finalSeriesLabels,
        finalSeriesLineFormats,
        finalSeriesPoints,
        limits,
        colorScale,
        true
      ),
      ...(chartConfig.value.gradientBelow
        ? createLineBottomGradients(finalSeriesLabels, gradientColors, finalSeriesPoints, limits)
        : []),
      ...createCircleHightlights(highlights, limits)
    ]
  })

  return {
    components,
    mergedData,
    measurements,
    resolutionButtons,
    selectedResolution,
    isLoading,
    updateViewport
  }
}

export function createTimeSeriesLegendComponents(
  chart: Chart<'timeseries'>,
  locale: string,
  labelConfig?: LabelConfig
): LegendComponent[] {
  const toCheckboxStyle = (style?: 'solid' | 'dashed' | 'dotted'): 'fill' | 'outline' => {
    if (style === 'dashed' || style === 'dotted') return 'outline'
    return 'fill'
  }

  return chart.labels.map((label, i) => ({
    type: 'checkbox',
    label: label[locale],
    labelConfig,
    styling: toCheckboxStyle(chart.lineFormats[i]?.style ?? 'solid'),
    color: chart.lineFormats[i]?.color ?? '#000000',
    initial: true
  }))
}
